/* eslint-disable */
import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Trash2 } from 'lucide-react';
import AdminLayout from './AdminLayout';
import { getAdminPhotos, getAdminCategories, bulkUploadPhotos, bulkUpdateCategory, deletePhoto } from '../config/api';

export default function Photos() {
  const [photos,     setPhotos]     = useState([]);
  const [categories, setCategories] = useState([]);
  const [catFilter,  setCatFilter]  = useState('');
  const [tab,        setTab]        = useState('all'); // 'all', 'default', 'published'
  const [files,      setFiles]      = useState([]);
  const [uploading,  setUploading]  = useState(false);
  const [uploadMsg,  setUploadMsg]  = useState('');
  const [uploadProgress, setUploadProgress] = useState('');
  const [deleteId,   setDeleteId]   = useState(null);
  const [selected,   setSelected]   = useState([]);
  const [moveCatId,  setMoveCatId]  = useState('');
  const [moving,     setMoving]     = useState(false);
  const fileRef = useRef();
  const [uploadAbort, setUploadAbort] = useState(null);

  const defaultCategory = categories.find(c => c.is_default === 1);

  const load = async () => {
    try {
      const [p, c] = await Promise.all([
        getAdminPhotos(catFilter || null),
        getAdminCategories()
      ]);
      setPhotos(Array.isArray(p.data?.data) ? p.data.data : []);
      setCategories(Array.isArray(c.data?.data) ? c.data.data : []);
    } catch (e) { console.error(e); }
  };

  useEffect(() => { load(); }, [catFilter]);

  const filteredPhotos = photos.filter(p => {
    if (tab === 'default') return p.category?.is_default === 1;
    if (tab === 'published') return p.category?.is_default === 0;
    return true;
  });

  const handleFiles = (e) => {
    const selectedFiles = Array.from(e.target.files || []);
    if (selectedFiles.length === 0) return;

    const validFiles = [];
    for (const f of selectedFiles) {
      if (!['image/jpeg', 'image/jpg', 'image/png'].includes(f.type)) {
        setUploadMsg(`${f.name}: Only JPEG and PNG allowed`);
        continue;
      }
      if (f.size > 25 * 1024 * 1024) {
        setUploadMsg(`${f.name}: File must be under 25 MB`);
        continue;
      }
      validFiles.push(f);
    }

    setFiles(validFiles);
    setUploadMsg('');
  };

  const handleBulkUpload = async () => {
    if (files.length === 0) { setUploadMsg('Please select files to upload'); return; }
    setUploading(true);
    setUploadMsg('');
    setUploadProgress('');

    // Create abort controller for cancellation
    const abortController = new AbortController();
    setUploadAbort(abortController);

    try {
      const fd = new FormData();
      files.forEach(f => fd.append('files', f));

      // Show warning for large uploads
      if (files.length > 50) {
        setUploadProgress(`⚠️ Uploading ${files.length} photos. This may take several minutes. Keep this page open.`);
      } else {
        setUploadProgress(`Uploading ${files.length} file${files.length > 1 ? 's' : ''}...`);
      }
      const response = await bulkUploadPhotos(fd, abortController.signal);

      const { succeeded, failed } = response.data.data;
      setFiles([]);
      if (fileRef.current) fileRef.current.value = '';

      if (failed.length === 0) {
        setUploadMsg(`✓ ${succeeded.length} photo${succeeded.length > 1 ? 's' : ''} uploaded successfully!`);
      } else {
        setUploadMsg(`✓ ${succeeded.length} succeeded, ${failed.length} failed`);
      }

      await load();
    } catch (e) {
      if (axios.isCancel(e) || e.name === 'AbortError') {
        setUploadMsg('⚠️ Upload cancelled. Some photos may have been uploaded. Refreshing...');
        await load(); // Refresh to show partial uploads
      } else {
        setUploadMsg(e.response?.data?.error || 'Upload failed. Please try again.');
      }
    } finally {
      setUploading(false);
      setUploadProgress('');
      setUploadAbort(null);
    }
  };

  const handleCancelUpload = () => {
    if (uploadAbort) {
      uploadAbort.abort();
      setUploadMsg('Cancelling upload...');
    }
  };

  const confirmDelete = async () => {
    if (!deleteId) return;
    try {
      if (Array.isArray(deleteId)) {
        await Promise.all(deleteId.map(id => deletePhoto(id)));
        setSelected([]);
      } else {
        await deletePhoto(deleteId);
      }
      setDeleteId(null);
      await load();
    } catch (e) { console.error(e); setDeleteId(null); }
  };

  const toggleSelect = (id) => setSelected(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id]);
  const toggleSelectAll = () => {
    if (selected.length === filteredPhotos.length) {
      setSelected([]);
    } else {
      setSelected(filteredPhotos.map(p => p.id));
    }
  };

  const handleBulkMove = async () => {
    if (selected.length === 0 || !moveCatId) return;
    setMoving(true);

    try {
      await bulkUpdateCategory(selected, moveCatId);
      const targetCat = categories.find(c => c.id === moveCatId);
      setUploadMsg(`✓ ${selected.length} photo${selected.length > 1 ? 's' : ''} moved to ${targetCat?.name}`);
      setSelected([]);
      setMoveCatId('');
      await load();
    } catch (e) {
      setUploadMsg(e.response?.data?.error || 'Move failed');
    } finally {
      setMoving(false);
    }
  };

  const inputStyle = {
    border: '1px solid var(--color-border)',
    background: 'var(--color-surface)',
    padding: '8px 12px',
    fontSize: 13,
    color: 'var(--color-text)',
    outline: 'none',
    fontFamily: 'var(--font-body)',
    width: '100%',
    borderRadius: 4,
  };

  const labelStyle = {
    fontSize: 10,
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    color: 'var(--color-muted)',
    display: 'block',
    marginBottom: 6,
    fontFamily: 'var(--font-body)',
  };

  const categoryBadgeStyle = (isDefault) => ({
    display: 'inline-block',
    padding: '4px 12px',
    borderRadius: 12,
    fontSize: 11,
    fontWeight: 500,
    background: isDefault ? '#fb923c' : 'var(--color-surface-alt)',
    color: isDefault ? '#fff' : 'var(--color-muted)'
  });

  return (
    <AdminLayout title="Photo Management">

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 20, borderBottom: '1px solid var(--color-border)' }}>
        {[
          { key: 'all', label: 'All Photos', count: photos.length },
          { key: 'default', label: 'Default (staging)', count: photos.filter(p => p.category?.is_default === 1).length },
          { key: 'published', label: 'Published', count: photos.filter(p => p.category?.is_default === 0).length }
        ].map(t => (
          <button
            key={t.key}
            onClick={() => { setTab(t.key); setSelected([]); }}
            style={{
              padding: '10px 16px',
              background: 'transparent',
              border: 'none',
              borderBottom: tab === t.key ? '2px solid var(--color-accent)' : '2px solid transparent',
              color: tab === t.key ? 'var(--color-text)' : 'var(--color-muted)',
              fontSize: 12,
              fontFamily: 'var(--font-body)',
              cursor: 'pointer',
              fontWeight: tab === t.key ? 500 : 400
            }}
          >
            {t.label} ({t.count})
          </button>
        ))}
      </div>

      {/* Bulk Upload */}
      <div style={{ background: 'var(--color-surface)', border: '1px solid var(--color-border)', borderRadius: 8, padding: 24, marginBottom: 28 }}>
        <div style={{ fontSize: 11, fontFamily: 'var(--font-body)', color: 'var(--color-muted)', marginBottom: 12, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
          Bulk Upload → Default Category (private staging)
        </div>

        <div
          onDragOver={e => e.preventDefault()}
          onDrop={e => {
            e.preventDefault();
            const droppedFiles = Array.from(e.dataTransfer.files || []);
            handleFiles({ target: { files: droppedFiles } });
          }}
          style={{ border: '2px dashed var(--color-border)', borderRadius: 6, padding: 28, textAlign: 'center', marginBottom: 16 }}
        >
          <div style={{ fontSize: 28, marginBottom: 12 }}>📁</div>
          <div style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--color-text)', marginBottom: 8 }}>
            {files.length > 0 ? `${files.length} file${files.length > 1 ? 's' : ''} selected` : 'Drag & drop files here'}
          </div>
          <div style={{ fontSize: 11, color: 'var(--color-muted)', fontFamily: 'var(--font-body)', marginBottom: 16 }}>JPEG or PNG · Max 25 MB per file</div>

          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            style={{
              padding: '12px 24px',
              background: 'var(--color-accent)',
              color: '#fff',
              border: 'none',
              fontSize: 12,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              cursor: 'pointer',
              fontFamily: 'var(--font-body)',
              borderRadius: 4,
              fontWeight: 500,
              transition: 'all 0.2s ease'
            }}
          >
            Browse Files
          </button>

          <input type="file" ref={fileRef} accept="image/jpeg,image/png" multiple style={{ display: 'none' }} onChange={handleFiles} />
        </div>

        <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
          <button
            onClick={handleBulkUpload}
            disabled={uploading || files.length === 0}
            style={{
              padding: '10px 20px',
              background: files.length === 0 ? 'rgba(119, 119, 119, 0.5)' : 'var(--color-accent)',
              color: files.length === 0 ? 'rgba(255, 255, 255, 0.5)' : '#fff',
              border: 'none',
              fontSize: 11,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              cursor: files.length === 0 ? 'not-allowed' : 'pointer',
              fontFamily: 'var(--font-body)',
              borderRadius: 4,
              whiteSpace: 'nowrap',
              opacity: uploading ? 0.7 : 1,
              transition: 'all 0.2s ease'
            }}
          >
            {uploading ? 'Uploading...' : `Upload ${files.length || ''} ${files.length === 1 ? 'Photo' : 'Photos'}`}
          </button>
          {uploading && (
            <button
              onClick={handleCancelUpload}
              style={{
                padding: '10px 20px',
                background: 'transparent',
                color: 'var(--color-error)',
                border: '1px solid var(--color-error)',
                fontSize: 11,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                cursor: 'pointer',
                fontFamily: 'var(--font-body)',
                borderRadius: 4,
                whiteSpace: 'nowrap',
                transition: 'all 0.2s ease'
              }}
            >
              Cancel Upload
            </button>
          )}
          {uploadProgress && (
            <span style={{ fontSize: 12, color: 'var(--color-muted)', fontFamily: 'var(--font-body)' }}>
              {uploadProgress}
            </span>
          )}
        </div>

        {uploadMsg && (
          <p style={{ marginTop: 12, fontSize: 12, fontFamily: 'var(--font-body)', color: uploadMsg.startsWith('✓') ? 'var(--color-success)' : 'var(--color-error)' }}>
            {uploadMsg}
          </p>
        )}
      </div>

      <div
        data-testid="photo-library-header"
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 18,
          flexWrap: 'wrap',
          gap: 14,
          ...(selected.length > 0 ? {
            border: '1px solid var(--color-accent)',
            borderRadius: 5,
            padding: '10px 14px',
            background: 'rgba(212, 168, 67, 0.05)',
          } : {}),
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <input
            type="checkbox"
            checked={selected.length === filteredPhotos.length && filteredPhotos.length > 0}
            onChange={toggleSelectAll}
            style={{ width: '16px', height: '16px', cursor: 'pointer' }}
          />
          <div style={{ fontFamily: 'var(--font-body)', fontSize: 16, fontWeight: 600, color: 'var(--color-text)' }}>
            Photo Library ({filteredPhotos.length})
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <select
            style={{ ...inputStyle, width: 'auto', padding: '10px 16px', fontSize: 13, minWidth: '200px', height: '42px' }}
            value={catFilter}
            onChange={e => { setCatFilter(e.target.value); setSelected([]); }}
          >
            <option value="">All categories</option>
            {categories.map(c => (
              <option key={c.id} value={c.id}>
                {c.name}{c.is_default === 1 ? ' (Default)' : ''}
              </option>
            ))}
          </select>

          {selected.length > 0 && (
            <div
              data-testid="bulk-action-bar"
              style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}
            >
              <span style={{ fontSize: 11, color: 'var(--color-accent)', fontWeight: 600, fontFamily: 'var(--font-body)', whiteSpace: 'nowrap' }}>
                {selected.length} selected
              </span>
              <select
                style={{ ...inputStyle, flex: 1, minWidth: 150, padding: '6px 10px', height: 'auto', fontSize: 11 }}
                value={moveCatId}
                onChange={e => setMoveCatId(e.target.value)}
              >
                <option value="">Move to category…</option>
                {categories.map(c => (
                  <option key={c.id} value={c.id}>
                    {c.name}{c.is_default === 1 ? ' (Default)' : ''}
                  </option>
                ))}
              </select>
              <button
                onClick={handleBulkMove}
                disabled={!moveCatId || moving}
                style={{
                  padding: '6px 14px',
                  background: moveCatId ? 'var(--color-accent)' : '#999',
                  color: '#fff',
                  border: 'none',
                  fontSize: 11,
                  cursor: moveCatId ? 'pointer' : 'not-allowed',
                  borderRadius: 4,
                  fontFamily: 'var(--font-body)',
                  fontWeight: 500,
                  whiteSpace: 'nowrap',
                }}
              >
                {moving ? 'Moving...' : 'Move'}
              </button>
              <button
                onClick={() => setDeleteId(selected)}
                style={{
                  padding: '6px 14px',
                  background: 'transparent',
                  color: 'var(--color-error)',
                  border: '1px solid var(--color-error)',
                  fontSize: 11,
                  cursor: 'pointer',
                  borderRadius: 4,
                  fontFamily: 'var(--font-body)',
                  whiteSpace: 'nowrap',
                }}
              >
                Delete ({selected.length})
              </button>
              <button
                onClick={() => setSelected([])}
                style={{
                  padding: '6px 12px',
                  background: 'transparent',
                  color: 'var(--color-muted)',
                  border: '1px solid var(--color-border)',
                  fontSize: 11,
                  cursor: 'pointer',
                  borderRadius: 4,
                  fontFamily: 'var(--font-body)',
                  whiteSpace: 'nowrap',
                }}
              >
                Cancel
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="photo-grid">
        {filteredPhotos.map(p => (
          <div
            key={p.id}
            data-testid="photo-tile"
            className={`photo-tile${selected.includes(p.id) ? ' selected' : ''}`}
            onClick={() => toggleSelect(p.id)}
          >
            <img
              src={p.thumbnail_url}
              alt={p.title || ''}
              style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
              loading="lazy"
            />
            <div className="photo-tile-check" data-testid="tile-checkbox">
              {selected.includes(p.id) && (
                <span style={{ color: '#fff', fontSize: 10, lineHeight: 1 }}>✓</span>
              )}
            </div>
            <button
              className="photo-tile-delete"
              data-testid="tile-delete-btn"
              aria-label="Delete photo"
              onClick={e => { e.stopPropagation(); setDeleteId(p.id); }}
            >
              ✕
            </button>
            <div
              className={`photo-tile-label${p.category?.is_default === 1 ? ' default' : ''}`}
              data-testid="tile-category-label"
            >
              {p.category?.name || ''}
            </div>
          </div>
        ))}
        {filteredPhotos.length === 0 && (
          <div style={{
            gridColumn: '1 / -1',
            padding: 32,
            textAlign: 'center',
            color: 'var(--color-muted)',
            fontFamily: 'var(--font-body)',
          }}>
            No photos yet. Upload your first photo above.
          </div>
        )}
      </div>

      {/* Mobile Card Layout */}
      <div className="admin-cards-mobile">
        {selected.length > 0 && (
          <button
            onClick={toggleSelectAll}
            style={{
              width: '100%',
              padding: '12px 16px',
              marginBottom: 12,
              background: 'var(--color-surface)',
              border: '1px solid var(--color-border)',
              borderRadius: 8,
              fontFamily: 'var(--font-body)',
              fontSize: 12,
              cursor: 'pointer',
              color: 'var(--color-text)',
            }}
          >
            {selected.length === filteredPhotos.length ? 'Deselect All' : 'Select All'}
          </button>
        )}

        {filteredPhotos.map(p => (
          <div key={p.id} className={`photo-card ${selected.includes(p.id) ? 'selected' : ''}`}>
            <input
              type="checkbox"
              className="photo-card-checkbox"
              checked={selected.includes(p.id)}
              onChange={() => toggleSelect(p.id)}
            />

            <img src={p.thumbnail_url} alt={p.title || ''} className="photo-card-thumb" />

            <div className="photo-card-info">
              <div className="photo-card-title">{p.title || 'Untitled'}</div>
              <div className="photo-card-meta">
                <span style={categoryBadgeStyle(p.category?.is_default === 1)}>
                  {p.category?.name}
                </span>
              </div>
              <div className="photo-card-date">
                {new Date(p.uploaded_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: '2-digit' })}
              </div>
            </div>

            <button
              className="photo-card-menu-btn"
              onClick={() => setDeleteId(p.id)}
              aria-label="Delete photo"
            >
              <Trash2 size={20} />
            </button>
          </div>
        ))}

        {filteredPhotos.length === 0 && (
          <div style={{ padding: 32, textAlign: 'center', color: 'var(--color-muted)', fontFamily: 'var(--font-body)' }}>
            No photos yet. Upload your first photo above.
          </div>
        )}
      </div>

      {/* Bulk Action Bar (Mobile) */}
      {selected.length > 0 && (
        <div className="bulk-action-bar active">
          <span className="bulk-action-count">{selected.length} selected</span>
          <select
            className="bulk-action-btn secondary"
            style={{ padding: '10px 12px', fontSize: 11, minWidth: '120px' }}
            value={moveCatId}
            onChange={e => setMoveCatId(e.target.value)}
          >
            <option value="">Move to...</option>
            {categories.map(c => (
              <option key={c.id} value={c.id}>
                {c.name}{c.is_default === 1 ? ' (Default)' : ''}
              </option>
            ))}
          </select>
          <button
            className="bulk-action-btn primary"
            onClick={handleBulkMove}
            disabled={!moveCatId || moving}
          >
            {moving ? 'Moving...' : 'Move'}
          </button>
          <button
            className="bulk-action-btn danger"
            onClick={() => setDeleteId(selected)}
          >
            Delete
          </button>
          <button
            className="bulk-action-btn secondary"
            onClick={() => setSelected([])}
          >
            Cancel
          </button>
        </div>
      )}

      {deleteId && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div className="delete-modal-content" style={{ background: 'var(--color-surface)', padding: 32, maxWidth: 420, width: '90%', borderRadius: 4 }}>
            <h3 style={{ fontFamily: 'var(--font-body)', fontSize: 16, fontWeight: 500, color: 'var(--color-text)', marginBottom: 10 }}>
              Delete {Array.isArray(deleteId) ? `${deleteId.length} photos` : 'this photo'}?
            </h3>
            <p style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--color-muted)', marginBottom: 24 }}>
              This cannot be undone.
            </p>
            <div className="delete-modal-buttons" style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button
                onClick={() => setDeleteId(null)}
                style={{ padding: '9px 20px', border: '1px solid var(--color-border)', background: 'transparent', fontSize: 12, cursor: 'pointer', fontFamily: 'var(--font-body)', borderRadius: 'var(--border-radius)' }}
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                style={{ padding: '9px 20px', background: 'var(--color-error)', color: '#fff', border: 'none', fontSize: 12, cursor: 'pointer', fontFamily: 'var(--font-body)', borderRadius: 'var(--border-radius)' }}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

    </AdminLayout>
  );
}
